import { useEffect, useMemo, useRef, useState } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import { X, Copy, RotateCcw, Minus, Plus, Info, Navigation, Loader2 } from 'lucide-react';
import type { Incident, RankedResponse, StationAvailability, TurnoutProvenance, CoverageBounds } from '@duty/shared';
import { MAPBOX_TOKEN } from '@/config/mapbox';
import { useTheme } from '@/theme/theme';
import { useCoverage, emptyCoverageOverrides, type CoverageOverrides, type CoverageQuery } from '@/hooks/useCoverage';
import { useStoredTurnoutOverrides, pickStationOverrides } from '@/hooks/useTurnoutOverrides';
import { candidateStationsNear, graphBounds, turnoutProfilesFor, cellSizeForBounds } from '@/incident/coverage';
import { fetchLiveDriveMinutes, fetchDirections, type DirectionRoute } from '@/incident/mapbox-directions';
import { useCoverageMap, assignStationColors, stationColor } from './useCoverageMap';

export function CoverageAnalysisView({ incident, onClose }: { incident: Incident; onClose: () => void }) {
  const { theme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [overrides, setOverrides] = useState<CoverageOverrides>(() => emptyCoverageOverrides());
  const [copied, setCopied] = useState(false);

  const gridBounds = useMemo(() => graphBounds(), []);
  const [viewportBounds, setViewportBounds] = useState<CoverageBounds>(gridBounds);
  const stations = useMemo(
    () => (incident.coordinates ? candidateStationsNear(incident.coordinates, { maxRadiusM: 100_000, maxCount: 12 }) : []),
    [incident.coordinates?.lng, incident.coordinates?.lat],
  );
  const { storedOverrides, saveOverride } = useStoredTurnoutOverrides();
  const baseTurnoutProfiles = useMemo(
    () => ({ ...turnoutProfilesFor(stations), ...pickStationOverrides(storedOverrides, stations) }), // shared SME defaults win over the static seed profile
    [stations, storedOverrides],
  );
  const cellSizeDeg = useMemo(() => cellSizeForBounds(viewportBounds), [viewportBounds]);

  const query = useMemo<CoverageQuery>(
    () => ({ stations, turnoutProfiles: baseTurnoutProfiles, incidentPoint: incident.coordinates }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stations, baseTurnoutProfiles, incident.coordinates?.lng, incident.coordinates?.lat],
  );
  const { responseOrder, grid, gridBorders, loading, graphSource, error } = useCoverage(query, overrides, gridBounds, cellSizeDeg);

  const colorMap = useMemo(() => assignStationColors(stations), [stations]);

  // "Refine" — one live, traffic-aware Mapbox Matrix lookup for the closest
  // three, on request only (never the default engine — see fetchLiveDriveMinutes).
  const [liveMinutes, setLiveMinutes] = useState<Map<string, number | undefined>>(new Map());
  const [directions, setDirections] = useState<DirectionRoute[]>([]);
  const [refining, setRefining] = useState(false);
  const [refineError, setRefineError] = useState<string | undefined>(undefined);

  const refineClosestThree = async () => {
    if (!incident.coordinates || !responseOrder) return;
    const coordById = new Map(stations.map((s) => [s.id, s.coord]));
    const closestThree = responseOrder.ranked
      .filter((r) => r.rank !== undefined && r.rank <= 3 && coordById.has(r.stationId))
      .map((r) => ({ stationId: r.stationId, coord: coordById.get(r.stationId)! }));
    if (closestThree.length === 0) return;
    setRefining(true);
    setRefineError(undefined);
    const [minutesResult, directionsResult] = await Promise.all([
      fetchLiveDriveMinutes(incident.coordinates, closestThree),
      fetchDirections(incident.coordinates, closestThree),
    ]);
    setRefining(false);
    if (!minutesResult) {
      setRefineError('Live traffic lookup unavailable — showing modelled estimates only.');
    } else {
      setLiveMinutes((prev) => {
        const next = new Map(prev);
        for (const r of minutesResult) next.set(r.stationId, r.minutes);
        return next;
      });
    }
    if (directionsResult) {
      setDirections(directionsResult);
    }
  };

  // Escape to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const mapRef = useCoverageMap({
    containerRef,
    theme,
    center: incident.coordinates ?? { lng: 0, lat: 0 },
    zoom: 10,
    incidentMarker: incident.coordinates,
    stations,
    grid,
    gridBorders,
  });

  // Update grid resolution based on map zoom/pan.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const onMoveEnd = () => {
      const b = map.getBounds();
      if (!b) return;
      setViewportBounds({ minLng: b.getWest(), minLat: b.getSouth(), maxLng: b.getEast(), maxLat: b.getNorth() });
    };
    map.on('moveend', onMoveEnd);
    if (map.isStyleLoaded()) onMoveEnd();
    else map.once('load', onMoveEnd);
    return () => {
      map.off('moveend', onMoveEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef]);

  // Draw refined directions on the map when available.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || directions.length === 0) return;
    const colorMap = assignStationColors(stations);
    const sourceId = 'refined-directions';
    const layerId = 'refined-directions-line';

    const geojson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: directions.map((d) => ({
        type: 'Feature' as const,
        geometry: d.geometry,
        properties: { stationId: d.stationId },
      })),
    };

    const addLayer = () => {
      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, { type: 'geojson', data: geojson });
        const colorExpr: unknown[] = ['match', ['get', 'stationId']];
        for (const s of stations) colorExpr.push(s.id, stationColor(s.id, colorMap));
        colorExpr.push('#9ca3af');
        map.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          paint: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            'line-color': colorExpr as any,
            'line-width': 3,
            'line-opacity': 0.8,
            'line-blur': 1.5,
          },
        });
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (map.getSource(sourceId) as any).setData(geojson);
      }
    };
    if (map.isStyleLoaded()) addLayer();
    else map.once('load', addLayer);
  }, [directions, stations]);

  const explanationText = responseOrder?.explanation;

  const copyExplanation = async () => {
    if (!explanationText) return;
    try {
      await navigator.clipboard.writeText(explanationText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — non-fatal, no explanation to give beyond the visible text.
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-bg" role="dialog" aria-modal="true" aria-label="Response coverage analysis">
      <div className="flex items-center justify-between border-b border-border bg-surface/70 px-4 py-2.5">
        <div>
          <h2 className="text-sm font-semibold">Response coverage analysis</h2>
          <p className="text-xs text-muted">{incident.location} — modelled, not live traffic. Advisory only.</p>
        </div>
        <button
          onClick={onClose}
          className="rounded-md border border-border bg-surface-2 p-1.5 text-muted hover:text-accent"
          aria-label="Close coverage analysis"
        >
          <X size={16} />
        </button>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[1fr_22rem]">
        <div className="relative min-h-[45vh] lg:min-h-0">
          {MAPBOX_TOKEN && incident.coordinates ? (
            <div ref={containerRef} className="h-full w-full" />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted">Map unavailable</div>
          )}
          {loading && (
            <div className="absolute left-3 top-3 rounded-md bg-bg/80 px-2 py-1 text-[11px] text-muted">Recomputing…</div>
          )}
        </div>

        <aside className="flex min-h-0 max-h-[42vh] flex-col overflow-y-auto border-t border-border p-3.5 lg:max-h-none lg:border-t-0 lg:border-l" style={{ paddingBottom: `max(0.875rem, env(safe-area-inset-bottom))` }}>
          {error && <p className="mb-2 text-xs text-crit">{error}</p>}

          {explanationText && (
            <div className="mb-3 rounded-md border border-med/40 bg-med/10 p-2.5 text-xs text-text">
              <p>{explanationText}</p>
              <button
                onClick={copyExplanation}
                className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-accent hover:underline"
              >
                <Copy size={11} /> {copied ? 'Copied' : 'Copy explanation'}
              </button>
            </div>
          )}

          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Estimated response order</h3>
            <button
              onClick={() => void refineClosestThree()}
              disabled={refining || !responseOrder}
              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-muted hover:text-accent disabled:opacity-40"
              title="Live traffic-aware drive time from Mapbox for the closest three stations"
            >
              {refining ? <Loader2 size={11} className="animate-spin" /> : <Navigation size={11} />} Refine
            </button>
          </div>
          {refineError && <p className="mb-2 text-[10px] text-med">{refineError}</p>}
          <ol className="space-y-2">
            {responseOrder?.ranked.map((r) => (
              <StationRow
                key={r.stationId}
                ranked={r}
                color={stationColor(r.stationId, colorMap)}
                liveMinutes={liveMinutes.get(r.stationId)}
                overrides={overrides}
                onOverridesChange={setOverrides}
                onSaveOverride={saveOverride}
              />
            ))}
          </ol>

          <div className="mt-3 flex items-center justify-between border-t border-border pt-2.5">
            <button
              onClick={() => setOverrides(emptyCoverageOverrides())}
              className="inline-flex items-center gap-1 text-[11px] text-muted hover:text-accent"
            >
              <RotateCcw size={11} /> Reset scenario
            </button>
          </div>

          <div className="mt-3 flex items-start gap-1.5 border-t border-border pt-2.5 text-[10px] leading-relaxed text-muted">
            <Info size={11} className="mt-0.5 shrink-0" />
            <span>
              Modelled travel time from a synthetic seed road graph — not live traffic, not brigade-confirmed turn-out.
              {graphSource ? ` Source: ${graphSource}.` : ''} See docs/response-coverage-2.0.md.
            </span>
          </div>
        </aside>
      </div>
    </div>
  );
}

function StationRow({
  ranked,
  color,
  liveMinutes,
  overrides,
  onOverridesChange,
  onSaveOverride,
}: {
  ranked: RankedResponse;
  color: string;
  /** Live, traffic-aware drive minutes from Mapbox (set only after "Refine" is clicked for this station). */
  liveMinutes?: number;
  overrides: CoverageOverrides;
  onOverridesChange: (o: CoverageOverrides) => void;
  onSaveOverride: (stationId: string, baseMinutes: number) => void;
}) {
  const override = overrides.turnout[ranked.stationId];
  const currentTurnout =
    override?.baseMinutes ?? (ranked.turnoutMinutes.state === 'known' ? ranked.turnoutMinutes.value! : undefined);
  const availability = overrides.availability[ranked.stationId]?.state ?? ranked.availability;

  const setTurnout = (minutes: number) => {
    const baseMinutes = Math.max(0, minutes);
    onOverridesChange({
      ...overrides,
      turnout: {
        ...overrides.turnout,
        [ranked.stationId]: {
          baseMinutes,
          provenance: 'sme-entered' as TurnoutProvenance,
          capturedAt: new Date().toISOString(),
        },
      },
    });
    onSaveOverride(ranked.stationId, baseMinutes);
  };

  const cycleAvailability = () => {
    const next: StationAvailability['state'] =
      availability === 'unknown' ? 'available' : availability === 'available' ? 'committed' : 'unknown';
    onOverridesChange({
      ...overrides,
      availability: { ...overrides.availability, [ranked.stationId]: { state: next, capturedAt: new Date().toISOString() } },
    });
  };

  return (
    <li
      className={`rounded-md border p-2 text-xs ${
        ranked.availability === 'committed' ? 'border-border/60 bg-surface-2/40 opacity-60' : 'border-border bg-surface-2/60'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color }} />
        <span className="min-w-0 flex-1 truncate font-medium">
          {ranked.rank ? `#${ranked.rank} ` : ''}
          {ranked.stationName}
        </span>
        {ranked.isNominalArea && <span className="chip shrink-0">nominal</span>}
      </div>
      <div className="mt-1 flex items-center justify-between text-[11px] text-muted">
        <span title={ranked.turnoutMinutes.note}>
          {ranked.driveMinutes !== undefined ? `${Math.round(ranked.driveMinutes)} min drive` : 'unreachable'}
          {ranked.driveMinutes !== undefined && (
            <span
              className="ml-1"
              title={
                ranked.driveConfidence === 'medium'
                  ? 'Modelled from the seeded road graph.'
                  : 'No road graph coverage here — straight-line distance estimate only.'
              }
            >
              ({ranked.driveConfidence === 'medium' ? 'modelled' : 'estimated'})
            </span>
          )}
          {' + '}
          {currentTurnout !== undefined
            ? `${Math.round(currentTurnout)} min turn-out (${ranked.turnoutMinutes.confidence} confidence)`
            : 'turn-out unknown'}
          {ranked.totalMinutes !== undefined && (
            <span className="ml-1 font-semibold text-text">= {Math.round(ranked.totalMinutes)} min</span>
          )}
        </span>
      </div>
      {liveMinutes !== undefined && (
        <div className="mt-0.5 flex items-center gap-1 text-[11px] text-accent">
          <Navigation size={10} />
          Live traffic: {Math.round(liveMinutes)} min drive (Mapbox, right now)
        </div>
      )}
      <div className="mt-1.5 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <button
            onClick={() => currentTurnout !== undefined && setTurnout(currentTurnout - 1)}
            className="rounded border border-border p-0.5 text-muted hover:text-accent"
            aria-label={`Decrease ${ranked.stationName} turn-out`}
          >
            <Minus size={10} />
          </button>
          <span className="w-6 text-center font-mono">{currentTurnout !== undefined ? Math.round(currentTurnout) : '—'}</span>
          <button
            onClick={() => setTurnout((currentTurnout ?? 0) + 1)}
            className="rounded border border-border p-0.5 text-muted hover:text-accent"
            aria-label={`Increase ${ranked.stationName} turn-out`}
          >
            <Plus size={10} />
          </button>
        </div>
        <button
          onClick={cycleAvailability}
          className={`rounded-full border px-2 py-0.5 text-[10px] capitalize ${
            availability === 'available'
              ? 'border-ok/50 bg-ok/10 text-ok'
              : availability === 'committed'
                ? 'border-med/50 bg-med/10 text-med'
                : 'border-border text-muted'
          }`}
        >
          {availability}
        </button>
      </div>
    </li>
  );
}
